/**
 * @file EDALab.js
 * @module src/js/EDALab
 * @description
 * **Defines the main routing structure of the EDALab website using React Router.**
 * - The routing includes public pages (homepage, advisor, members, etc.) and nested routes for a protected member zone.
 * - This component assumes that `<BrowserRouter />` is already provided at a higher level (e.g., in `<ContextProviders />`).
 * - An `ErrorBoundary` is implemented to catch and display errors during rendering of any child components.
 *
 * @version
 * <p>
 *  2025-07-22: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-29: Add ErrorBoundary for better error handling. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from "react-error-boundary";
import ContextProviders from './contexts/ContextProviders';
import EDALabLayout from './layouts/EDALabLayout';
import Homepage from './pages/Homepage';
import BannerPhotoGalleryPage from './pages/BannerPhotoGalleryPage';
import AdvisorPage from './pages/AdvisorPage';
import MembersPage from './pages/MembersPage';
import ProjectsPage from './pages/ProjectsPage';
import PublicationsPage from './pages/PublicationsPage';
import DownloadsPage from './pages/DownloadsPage';
import AcitivityPhotoGalleryPage from './pages/AcitivityPhotoGalleryPage';
import MemberZoneLayout from './layouts/MemberZoneLayout';
import ProfilePage from './pages/memberZone/ProfilePage';
import ContactPage from './pages/memberZone/ContactPage';
import ArchivesPage from './pages/memberZone/ArchivesPage';
import LabDataPage from './pages/memberZone/LabDataPage';
import PaperReviewPage from './pages/memberZone/PaperReviewPage';
import NewMemberPage from './pages/memberZone/NewMemberPage';
import LogInPage from './pages/LogInPage';
import NotFoundPage from './pages/NotFoundPage';
import errorImg from '../images/undraw_server-error_syuz.png';

export default function EDALab() {
    return (
        <ErrorBoundary FallbackComponent={FallbackComponent}>
            <ContextProviders>
                <Routes> {/* BrowserRouter (as Router) is included in ContextProviders */}
                    <Route path="/" element={<EDALabLayout />}>
                        <Route index element={<Homepage />} />
                        <Route path="advisor" element={<AdvisorPage />} />
                        <Route path="members" element={<MembersPage />} />
                        <Route path="projects" element={<ProjectsPage />} />
                        <Route path="publications" element={<PublicationsPage />} />
                        <Route path="downloads" element={<DownloadsPage />} />
                        <Route path="activity-photo-gallery" element={<AcitivityPhotoGalleryPage />} />
                        <Route path="banner-photo-gallery" element={<BannerPhotoGalleryPage />} />
                        <Route path="member-zone" element={<MemberZoneLayout />}>
                            <Route path="profile" element={<ProfilePage />} />
                            <Route path="contact" element={<ContactPage />} />
                            <Route path="archives" element={<ArchivesPage />} />
                            <Route path="lab-data" element={<LabDataPage />} />
                            <Route path="paper-review" element={<PaperReviewPage />} />
                            <Route path="new-member" element={<NewMemberPage />} />
                        </Route>
                        <Route path="login" element={<LogInPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </Route>
                </Routes>
            </ContextProviders>
        </ErrorBoundary>
    );
}

function FallbackComponent({ error, resetErrorBoundary }) {
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center', // vertical centering
            alignItems: 'center',     // horizontal centering
            height: '100vh',          // full viewport height
            padding: '2rem',
            textAlign: 'center',
        }}>
            <img 
                src={errorImg}
                alt="Error" 
                style={{ width: '30vw', marginBottom: '1.5rem' }} 
            />
            <h2>Something went wrong:</h2>
            <pre style={{
                whiteSpace:'pre-wrap',
                wordWrap: 'break-word',
                maxWidth: '50vw',
                maxHeight: '20vh',
                overflowY: 'auto',
            }}>{error.message}</pre>
            <button onClick={resetErrorBoundary}>Try again</button>
        </div>
    );
}